import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { BcRestApi } from 'common/api/rest-api';
import { GetSalesforceReferenceItemsResult, SalesforceReferenceListItem } from 'common/api/models/salesforce.models';
import { SalesforceField, SalesforceFieldTypes, SalesforceReferenceFieldDefaultValue } from '../../model/salesforce-form.model';
import { ListItem } from './salesforce-reference-field.model';
import { SalesforceErrorService } from '../../../salesforce-error/salesforce-error.service';
import { BcAnalytics } from '../../../../../../common/analytics/analytics.service';

@Component({
  selector: 'app-salesforce-reference-field',
  templateUrl: './salesforce-reference-field.component.html',
  styleUrls: ['./salesforce-reference-field.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SalesforceReferenceFieldComponent implements OnInit {
  @Input() chatId: string;
  @Input() field: SalesforceField;
  @Input() form: UntypedFormGroup;
  @Input() context: string;
  @Input() errorText: string;

  @ViewChild('inputElement', { static: true }) inputEl: ElementRef;

  public defaultItem: ListItem;
  public selectedReference: string;
  public multilineEnabled: boolean;

  private _lastSelectedItem: ListItem;
  private readonly groupReference: string = 'Group';
  private readonly accountReference: string = 'Account';
  private readonly emptyItem: ListItem = {
    label: '',
    value: ''
  };

  constructor(private _bcRestApi: BcRestApi,
              private _salesforceErrorService: SalesforceErrorService,
              private _bcAnalytics: BcAnalytics) {
  }

  public ngOnInit(): void {
    if (this.field.type !== SalesforceFieldTypes.REFERENCE) {
      throw new Error('The specified field must be a Reference field!');
    }

    if (!this.field.referenceTo) {
      throw new Error('The referenceTo property is required for a reference field!');
    }

    if (this.field.referenceTo.length === 0) {
      throw new Error('The referenceTo property must refer to at least one other object!');
    }

    if (this.field.value) {
      const defaultValue: SalesforceReferenceFieldDefaultValue = <SalesforceReferenceFieldDefaultValue> this.field.value;
      if (defaultValue.name && defaultValue.id) {
        this.defaultItem = {
          label: defaultValue.name,
          value: defaultValue.id
        };
        this._setFieldValue(this.defaultItem);
      }
    }

    if (this.field.referenceTo && this.isSelectorAvailable) {
      this.selectedReference = this.field.referenceTo[0].value;
    }
  }

  get isSelectorAvailable(): boolean {
    return this.field.referenceTo.length > 1;
  }

  get isValid() {
    return this.form.controls[this.field.name].valid;
  }

  public onBlur() {
    const currentLabel: string = this._getFieldLabel();
    const currentValue: string = this._getFieldValue();

    // If the user cleared the previously entered label, clear the previously set ID.
    if (currentLabel === '') {
      this._setFieldValue(this.emptyItem);
      return;
    }

    // If the user previously selected a valid item from the pop-up list.
    if (this._lastSelectedItem) {
      // If the user entered a new label, but the selected ID remained the same, restore the label to the last selected label.
      if (currentLabel !== this._lastSelectedItem.label &&
        currentValue === this._lastSelectedItem.value) {
        this._setFieldLabel(this._lastSelectedItem.label);
      }
    } else {
      // If the user has not selected any valid value from the pop-up list before, then clear the entered label, because we don't have an ID for it.
      this._setFieldLabel('');
      this._setFieldValue(this.emptyItem);
    }
  }

  public onItemSelected(selectedItem: ListItem) {
    if (!selectedItem) {
      throw new Error('Please specify the selectedItem!');
    }

    this._setFieldValue(selectedItem);
  }

  public onSearch = (searchText: Observable<string>): Observable<ListItem[]> => {
    return searchText
      .pipe(debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => {
        // Salesforce requires minimum 2 characters for the search term.
        if (term.trim().length < 2) {
          return Promise.resolve([]);
        } else {
          const referencedObjectInternalName: string = this.isSelectorAvailable
            ? this.selectedReference
            : this.field.referenceTo[0].value || this.field.referenceTo[0].name;

          this.multilineEnabled = referencedObjectInternalName === this.accountReference;

          return this._bcRestApi.getSalesforceReferenceItems(referencedObjectInternalName, term, referencedObjectInternalName === this.groupReference ? this.context : undefined)
            .then((results: GetSalesforceReferenceItemsResult) => {
              this._salesforceErrorService.clearError(this.chatId);
              return results.items.map((result: SalesforceReferenceListItem) => <ListItem> {
                value: result.id,
                label: result.name
              });
            }).catch(error => {
              this._salesforceErrorService.notifyError(this.chatId);
              this._bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
              return Promise.resolve([]);
            });
        }
      }));
  };

  public formatItemWhenSelected(item: ListItem): string {
    return item.label;
  }

  public onReferenceChange(): void {
    this._clearFieldValues();

    setTimeout(() => {
      this.inputEl.nativeElement.focus();
    }, 150);
  }

  private _clearFieldValues(): void {
    this._setFieldValue(this.emptyItem);
    this._setFieldLabel('');
  }

  private _getFieldLabel(): string {
    return this.inputEl.nativeElement.value;
  }

  private _setFieldLabel(label: string): void {
    this.inputEl.nativeElement.value = label;

    // Notify the ngbTypeahead control about the change. Without this the control would restore the previous value when the popup is dismissed.
    // NOTE: IE11 does not support the "new Event" constructor.
    const inputEvent = document.createEvent('Event');
    inputEvent.initEvent('input', true, true);
    this.inputEl.nativeElement.dispatchEvent(inputEvent);
  }

  private _getFieldValue(): string {
    return this.form.controls[this.field.name].value;
  }

  private _setFieldValue(item: ListItem): void {
    if (!item) {
      throw new Error('Please specify the item!');
    }

    this.form.controls[this.field.name].setValue(item.value);
    this._lastSelectedItem = item;
  }
}


